import { BreadcrumbsProps } from "components/BreadCrumb";
import { Form } from "components/Form";
import { Page } from "components/Page";
import { Stepper, StepProps } from "components/Steps";
import { YupCore } from "core/yup";
import * as DateFns from "date-fns";
import { useMemo } from "react";
import { isValidCpf } from "utils/validators";
import { FinancingDataForm } from "./FinancingDataForm";
import { PersonalDataForm } from "./PersonalDataForm";
import { PropertyTypeForm } from "./PropertyTypeForm";
import { ResultFinance } from "./ResultFinance";

export interface SimulateFinanceData {
  amortizationType: string;
  approximateAmount: string;
  city: string;
  countMonths: string;
  cpf: string;
  dateBirthday: string;
  deadline: string;
  email: string;
  financingAmount: string;
  name: string;
  phoneNumber: string;
  propertyType: string;
}

export function SimulatorFinance() {
  const steps = useMemo(() => {
    const values: StepProps[] = [
      { title: "Tipo de Imóvel", content: <PropertyTypeForm /> },
      {
        title: "Dados do financiamento",
        content: <FinancingDataForm />,
      },
      { title: "Dados pessoais", content: <PersonalDataForm /> },
      { title: "Resultado", content: <ResultFinance /> },
    ];
    return values;
  }, []);

  const validateFinancingWithApproximateAmount = useMemo(() => {
    return {
      test: function (value: any, ctx: any) {
        const approximateAmount = ctx.parent.approximateAmount;
        const financingAmount = value;

        if (!financingAmount) {
          return true;
        }

        const amountConverted =
          Number(approximateAmount) - Number(approximateAmount) * 0.2; //valor do emprestimo nao pode passar dos 80% do imóvel

        if (amountConverted < Number(financingAmount)) {
          return false;
        }

        return true;
      },
      message: `O valor não deve ser maior que 80% do valor do imóvel`,
    };
  }, []);

  const formSchema = useMemo(() => {
    return YupCore.object().shape({
      city: YupCore.object().required(),
      approximateAmount: YupCore.number()
        .min(5000000, "Valor mínimo: 50.000,00")
        .max(1000000000, "Valor máximo: 10.000.000,00")
        .required(),
      financingAmount: YupCore.number()
        .min(5000000, "Valor mínimo: 50.000,00")
        .test(validateFinancingWithApproximateAmount)
        .max(1000000000, "Valor máximo: 10.000.000,00")
        .required(),
      dateBirthday: YupCore.date()
        .required()
        .nullable()
        .max(
          DateFns.subYears(new Date(), 18),
          "É necessário ter no minímo 18 anos"
        )
        .min(
          DateFns.subYears(new Date(), 77),
          "É necessário ter menos de 77 anos"
        ),
      deadline: YupCore.object().required(),
      amortizationType: YupCore.string().required(),
      name: YupCore.string().required(),
      email: YupCore.string().email().required(),
      phoneNumber: YupCore.string().required(),
      cpf: YupCore.string()
        .nullable(true)
        .test("invalid-cnpj", "CPF inválido", function (value: any) {
          return !value ? true : isValidCpf(value);
        }),
    });
  }, [validateFinancingWithApproximateAmount]);

  const breadcrumbs: BreadcrumbsProps = useMemo(() => {
    return {
      paths: [
        {
          link: "/",
          title: "Página inicial",
        },
        {
          link: "/modalidade",
          title: "Modalidade",
        },
      ],
      currentPage: "Financiamento",
    };
  }, []);

  return (
    <Page breadcrumbs={breadcrumbs}>
      <Form
        initialSchemaValidation={formSchema}
        initialValues={{ countMonths: 12 }}
        onSubmit={() => console.log()}
      >
        <Stepper steps={steps} />
      </Form>
    </Page>
  );
}
